import {
  useState,
  useRef,
  useEffect,
  forwardRef,
  type InputHTMLAttributes,
} from 'react';
import { cn, useTheme } from '@vitness/theme';
import { Search, X } from '@vitness/icons';

export interface SearchInputProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size' | 'value' | 'onChange' | 'type'> {
  /** Controlled value. */
  value: string;
  /** Fires on every keystroke with the next raw value. */
  onChange: (value: string) => void;
  /**
   * Optional debounced callback. Fires `delay` ms after the last change (and
   * immediately on clear). Use this to drive expensive work (queries, filters)
   * while `onChange` keeps the field responsive.
   */
  onDebouncedChange?: (value: string) => void;
  /** Debounce delay in ms for `onDebouncedChange`. Defaults to 250. */
  debounceMs?: number;
  /** Accessible label for the clear button. Defaults to "Clear search". */
  clearLabel?: string;
}

/**
 * Search field with a leading search icon and a trailing clear (×) button that
 * appears once there is a value. Controlled via `value`/`onChange`. The clear
 * button focuses the input after clearing so keyboard users keep their place.
 *
 * Debouncing is opt-in: pass `onDebouncedChange` (and optionally `debounceMs`)
 * to receive a settled value without baking a timer into every consumer.
 */
export const SearchInput = forwardRef<HTMLInputElement, SearchInputProps>(function SearchInput(
  {
    value,
    onChange,
    onDebouncedChange,
    debounceMs = 250,
    clearLabel = 'Clear search',
    className,
    placeholder = 'Search…',
    disabled,
    ...props
  },
  forwardedRef,
) {
  const { accentSafe } = useTheme();
  const innerRef = useRef<HTMLInputElement>(null);
  const [focused, setFocused] = useState(false);

  const setRefs = (node: HTMLInputElement | null) => {
    innerRef.current = node;
    if (typeof forwardedRef === 'function') forwardedRef(node);
    else if (forwardedRef) forwardedRef.current = node;
  };

  // Fire the debounced callback after the value settles. Keyed on `value` so a
  // clear (which also changes `value`) flows through the same path.
  useEffect(() => {
    if (!onDebouncedChange) return;
    const id = setTimeout(() => onDebouncedChange(value), debounceMs);
    return () => clearTimeout(id);
  }, [value, debounceMs, onDebouncedChange]);

  const handleClear = () => {
    onChange('');
    innerRef.current?.focus();
  };

  return (
    <div
      className={cn(
        'relative flex items-center rounded-vn-md border bg-surface-input transition-colors',
        disabled && 'opacity-60',
        className,
      )}
      style={{ borderColor: focused ? accentSafe : 'var(--vn-border-default)' }}
    >
      <span className="pointer-events-none absolute left-2.5 flex text-text-muted">
        <Search size={14} aria-hidden="true" />
      </span>
      <input
        ref={setRefs}
        type="search"
        role="searchbox"
        value={value}
        disabled={disabled}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        onFocus={(e) => {
          setFocused(true);
          props.onFocus?.(e);
        }}
        onBlur={(e) => {
          setFocused(false);
          props.onBlur?.(e);
        }}
        className={cn(
          'w-full bg-transparent font-body text-text-primary outline-none',
          'pl-8 pr-8 py-1.5 text-xs',
          'placeholder:text-text-muted',
          // Hide the native WebKit clear affordance; we render our own.
          '[&::-webkit-search-cancel-button]:appearance-none',
        )}
        {...props}
      />
      {value && !disabled && (
        <button
          type="button"
          onClick={handleClear}
          aria-label={clearLabel}
          className={cn(
            'absolute right-2 flex items-center justify-center rounded-vn-sm',
            'text-text-muted hover:text-text-primary outline-none',
            'focus-visible:ring-2',
          )}
          style={{ '--tw-ring-color': accentSafe } as React.CSSProperties}
        >
          <X size={14} aria-hidden="true" />
        </button>
      )}
    </div>
  );
});
